import { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useWallet } from '../../context/WalletContext';
import { useConnectModal, useWalletDetailsModal } from 'thirdweb/react';
import { thirdwebClient, supportedWallets } from '../../lib/thirdweb';
import { useTranslation } from 'react-i18next';
import LangSwitcher from '../LangSwitcher';

/* Unique gradient avatar from wallet address */
function WalletAvatar({ address, size = 22 }) {
  const hash = address ? address.toLowerCase().replace(/^0x/, '') : '000000';
  const h1 = parseInt(hash.slice(0, 4), 16) % 360;
  const h2 = (h1 + 55 + (parseInt(hash.slice(4, 8), 16) % 50)) % 360;
  const s1 = 65 + (parseInt(hash.slice(8, 10), 16) % 20);
  const s2 = 70 + (parseInt(hash.slice(10, 12), 16) % 20);
  const angle = parseInt(hash.slice(12, 14), 16) % 360;
  const bg = `linear-gradient(${angle}deg, hsl(${h1},${s1}%,52%), hsl(${h2},${s2}%,44%))`;
  return (
    <span
      className="app-wallet-avatar"
      style={{ background: bg, width: size, height: size, minWidth: size }}
    />
  );
}

/* Mobile lang switcher — matches vitrine style exactly */
function MobileLangSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = i18n.language?.slice(0, 2) ?? 'en';
  const langs = [
    { code: 'en', label: 'EN', flag: 'fi-gb' },
    { code: 'fr', label: 'FR', flag: 'fi-fr' },
  ];
  return (
    <div className={`app-mob-lang${open ? ' open' : ''}`}>
      <i className="ph ph-globe app-mob-globe" onClick={() => setOpen(o => !o)} />
      <div className="app-mob-lang-drop">
        {langs.map(({ code, label, flag }) => (
          <button
            key={code}
            className={`app-mob-lang-opt${current === code ? ' active' : ''}`}
            onClick={() => { i18n.changeLanguage(code); setOpen(false); }}
          >
            <span className={`fi ${flag}`} />
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AppLayout({ children }) {
  const { t } = useTranslation('common');
  const { theme, toggleTheme } = useTheme();
  const { walletAddress, unreadCount, markAllRead, notifications } = useWallet();
  const detailsModal = useWalletDetailsModal();
  const { connect } = useConnectModal();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const bellRef = useRef(null);

  const NAV_ITEMS = [
    { path: '/strategies',    icon: 'ph-squares-four',            label: t('nav.strategies') },
    { path: '/dashboard',     icon: 'ph-chart-pie-slice',         label: t('nav.dashboard') },
    { path: '/transactions',  icon: 'ph-clock-counter-clockwise', label: t('nav.transactions') },
    { path: '/settings',      icon: 'ph-gear',                    label: t('nav.settings') },
  ];

  const shortAddr = walletAddress ? `0x…${walletAddress.slice(-3)}` : null;

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!notifOpen) return;
    function handle(e) {
      if (bellRef.current && !bellRef.current.contains(e.target)) setNotifOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [notifOpen]);

  function openWalletModal() {
    detailsModal.open({ client: thirdwebClient, theme: theme === 'dark' ? 'dark' : 'light' });
  }

  async function handleConnect() {
    try {
      await connect({ client: thirdwebClient, wallets: supportedWallets, showThirdwebBranding: false, title: 'Connect to G12 Labs' });
    } catch (err) {
      if (err?.message !== 'Connection closed') console.error(err);
    }
  }

  function handleBellClick() {
    setNotifOpen(o => !o);
    if (unreadCount > 0) markAllRead();
  }

  return (
    <div className="app-shell">

      {/* ── Top nav ── */}
      <header className="app-nav">
        <div className="app-nav-inner">

          <a href="https://g12labs.com" target="_blank" rel="noopener noreferrer" className="app-nav-logo">
            <img src="/logo_g12.svg" alt="G12 Labs" className="app-nav-logo-img" />
          </a>

          <nav className="app-nav-tabs">
            {NAV_ITEMS.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `app-nav-tab${isActive ? ' active' : ''}`}
              >
                <i className={`ph-bold ${item.icon}`} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="app-nav-actions">
            <LangSwitcher />

            <button className="app-icon-btn app-theme-btn" onClick={toggleTheme} title="Toggle theme">
              <AnimatePresence mode="wait" initial={false}>
                <motion.i
                  key={theme}
                  className={`ph ${theme === 'dark' ? 'ph-moon' : 'ph-sun'}`}
                  initial={{ opacity: 0, rotate: -90, scale: 0.5 }}
                  animate={{ opacity: 1, rotate: 0,   scale: 1   }}
                  exit={{    opacity: 0, rotate:  90, scale: 0.5 }}
                  transition={{ duration: 0.2, ease: [0.34, 1.56, 0.64, 1] }}
                />
              </AnimatePresence>
            </button>

            {/* Bell */}
            {walletAddress && (
              <div className="app-bell-wrap" ref={bellRef}>
                <button className="app-icon-btn" onClick={handleBellClick} title={t('notifications.title')}>
                  <i className="ph ph-bell" />
                </button>
                {unreadCount > 0 && (
                  <span className="app-bell-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}

                {notifOpen && (
                  <div className="app-notif-panel">
                    <div className="app-notif-panel-header">
                      <span>{t('notifications.title')}</span>
                    </div>
                    {notifications.length === 0 ? (
                      <div className="app-notif-empty">
                        <i className="ph ph-bell-slash" />
                        <span>{t('notifications.empty')}</span>
                      </div>
                    ) : (
                      <div className="app-notif-list">
                        {notifications.slice(0, 8).map((n, i) => (
                          <div key={n.id ?? i} className={`app-notif-item ${!n.read ? 'unread' : ''}`}>
                            <div className="app-notif-item-title">{n.title ?? n.type ?? 'Notification'}</div>
                            {n.message && <div className="app-notif-item-msg">{n.message}</div>}
                            {n.created_at && (
                              <div className="app-notif-item-time">
                                {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Wallet — desktop only */}
            {walletAddress ? (
              <button className="app-wallet-btn" onClick={openWalletModal}>
                <WalletAvatar address={walletAddress} />
                <span>{shortAddr}</span>
              </button>
            ) : (
              <button className="app-connect-btn" onClick={handleConnect}>
                <i className="ph-bold ph-wallet" />
                {t('wallet.connect')}
              </button>
            )}

            <button
              className={`app-burger${mobileMenuOpen ? ' open' : ''}`}
              onClick={() => setMobileMenuOpen(prev => !prev)}
              aria-label="Menu"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile menu ── */}
      {mobileMenuOpen && (
        <div className="app-mobile-menu">
          <nav className="app-mobile-links">
            {NAV_ITEMS.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `app-mobile-link${isActive ? ' active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className={`ph-bold ${item.icon}`} />
                <span>{item.label}</span>
              </NavLink>
            ))}
            <MobileLangSwitcher />
          </nav>
          <div className="app-mobile-bottom">
            {walletAddress ? (
              <button className="app-mobile-wallet" onClick={() => { setMobileMenuOpen(false); openWalletModal(); }}>
                <WalletAvatar address={walletAddress} size={26} />
                <span className="app-mobile-addr">{shortAddr}</span>
              </button>
            ) : (
              <button className="app-connect-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={() => { setMobileMenuOpen(false); handleConnect(); }}>
                <i className="ph-bold ph-wallet" /> {t('wallet.connectWallet')}
              </button>
            )}
          </div>
        </div>
      )}

      <main className="app-content">
        {children}
      </main>
    </div>
  );
}
